// ADDED: the one gate in front of the friends features.
//
// Usernames arrived after these accounts did, so an existing collector has none. Rather
// than inventing one from their email address - which would leak part of it to everyone
// who can search - the app asks for one the next time they sign in, and nothing else is
// reachable until they choose. New accounts go through the same prompt, so there is one
// path to a username rather than two.
import React, { useState } from 'react';

import axios, { getErrorMessage } from '../../api/axios';
import { useAuth } from './AuthProvider';

export default function UsernameGate() {
    const { isAuthenticated, user, updateUser } = useAuth();
    const [username, setUsername] = useState('');
    const [error, setError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Nothing to ask while signed out, still loading, or once a username exists
    if (!isAuthenticated || !user || user.username) return null;

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setIsSaving(true);

        try {
            const response = await axios.put('/me/username', { username: username.trim() });
            updateUser(response.data.user); // this unmounts the gate
        } catch (err) {
            setError(getErrorMessage(err, 'Could not save that username.'));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        // No close button and no way past it on purpose - this is a gate, not a suggestion
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
            <div className="w-full max-w-md cv-card overflow-hidden animate-fade-up">
                <div className="cv-stripe" />
                <form onSubmit={handleSubmit} className="px-5 py-6 sm:px-6 space-y-4">
                    <div className="space-y-2">
                        <p className="cv-eyebrow">One quick thing</p>
                        <h1 className="cv-display text-2xl">Choose a username</h1>
                        <p className="text-sm cv-muted leading-relaxed">
                            This is how other collectors find you and add you as a friend.
                            Your email address is never shown to anyone.
                        </p>
                    </div>

                    <div>
                        <label htmlFor="username" className="cv-label">Username</label>
                        <input
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            type="text"
                            autoComplete="off"
                            autoFocus
                            maxLength={20}
                            placeholder="e.g. redline_hunter"
                            className="cv-input"
                        />
                        <p className="text-xs cv-muted mt-1.5">
                            3 to 20 characters. Letters, numbers and underscores.
                        </p>
                    </div>

                    {error && <p className="cv-alert-error text-center">{error}</p>}

                    <button
                        type="submit"
                        disabled={isSaving || username.trim().length < 3}
                        className="cv-btn cv-btn-primary w-full py-3 uppercase disabled:opacity-50 disabled:cursor-not-allowed">
                        {isSaving ? 'Saving...' : 'Continue'}
                    </button>
                </form>
            </div>
        </div>
    );
}
